import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, Badge, Modal, Field, Input, Select } from "@/components/ui-kit";
import { fmtDate } from "@/lib/utils";
import { BookText, ListTree, Download, ScrollText, Plus, Trash2, Check, Lock, RotateCcw, Pencil, ShieldCheck, AlertTriangle, Scale } from "lucide-react";
import { toast } from "sonner";

const JOURNALS = { recettes: "Recettes", depenses: "Dépenses", financier: "Financier", od: "Opérations diverses" };
const KLASS_LABEL = { 1: "1 · Fonds propres & dettes LT", 2: "2 · Immobilisations", 3: "3 · Stocks", 4: "4 · Créances & dettes", 5: "5 · Valeurs disponibles", 6: "6 · Charges", 7: "7 · Produits" };
const euro = (n) => `${(Number(n) || 0).toFixed(2)} €`;
const currentYear = String(new Date().getFullYear());

export default function Comptabilite() {
  const [tab, setTab] = useState("journaux");
  const TABS = [
    { k: "journaux", label: "Journaux", Icon: BookText },
    { k: "plan", label: "Plan comptable", Icon: ListTree },
    { k: "etats", label: "Bilan & Résultats", Icon: Scale },
    { k: "import", label: "Importer", Icon: Download },
    { k: "audit", label: "Piste d'audit", Icon: ScrollText },
  ];
  return (
    <div data-testid="comptabilite-page">
      <PageHeader title="Comptabilité" subtitle="Comptabilité en partie double (ASBL) — journaux, plan comptable et piste d'audit." />
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`compta-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            <t.Icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "journaux" && <Journaux />}
      {tab === "plan" && <PlanComptable />}
      {tab === "etats" && <Etats />}
      {tab === "import" && <Importer />}
      {tab === "audit" && <AuditTrail />}
    </div>
  );
}

/* ---------------- Journaux / écritures ---------------- */
function Journaux() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [journal, setJournal] = useState("");
  const [year, setYear] = useState(currentYear);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (journal) params.journal = journal;
    if (year) params.year = year;
    if (status) params.status = status;
    api.get("/accounting/entries", { params }).then((r) => setEntries(r.data));
  };
  useEffect(() => { api.get("/accounting/accounts").then((r) => setAccounts(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [journal, year, status]);

  const validate = async (id) => { try { await api.post(`/accounting/entries/${id}/validate`); toast.success("Écriture validée"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const reverse = async (id) => { if (!window.confirm("Créer une écriture de contre-passation ?")) return; try { await api.post(`/accounting/entries/${id}/reverse`); toast.success("Contre-passation créée"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const remove = async (id) => { if (!window.confirm("Supprimer ce brouillon ?")) return; try { await api.delete(`/accounting/entries/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };

  return (
    <div data-testid="compta-journaux">
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-48"><label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Journal</label>
            <Select data-testid="journal-filter" value={journal} onChange={(e) => setJournal(e.target.value)}><option value="">Tous</option>{Object.entries(JOURNALS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></div>
          <div className="w-28"><label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Exercice</label>
            <Input data-testid="year-filter" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" /></div>
          <div className="w-40"><label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Statut</label>
            <Select data-testid="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Tous</option><option value="draft">Brouillon</option><option value="validated">Validé</option></Select></div>
          <Button data-testid="new-entry-btn" className="ml-auto" onClick={() => setOpen(true)}><Plus size={16} /> Nouvelle écriture</Button>
        </div>
      </Card>

      {entries.length ? (
        <div className="space-y-3">
          {entries.map((e) => <EntryCard key={e.id} e={e} onValidate={() => validate(e.id)} onReverse={() => reverse(e.id)} onDelete={() => remove(e.id)} />)}
        </div>
      ) : <Empty text="Aucune écriture. Créez une écriture ou importez depuis le Comité / Financier." />}

      {open && <EntryModal accounts={accounts} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function EntryCard({ e, onValidate, onReverse, onDelete }) {
  const validated = e.status === "validated";
  return (
    <Card data-testid="entry-card" className={validated ? "border-l-4 border-l-primary" : "border-l-4 border-l-yellow-500/60"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge color="gray">{JOURNALS[e.journal] || e.journal}</Badge>
            <span className="font-bold">{fmtDate(e.date)}</span>
            {e.ref && <span className="text-xs text-muted-foreground">réf. {e.ref}</span>}
            {validated ? <Badge color="green"><Lock size={11} /> Validé</Badge> : <Badge color="yellow">Brouillon</Badge>}
            {e.reversed && <Badge color="red">Contre-passé</Badge>}
            {e.source && e.source !== "manual" && <Badge color="blue">{e.source === "reversal" ? "Extourne" : `Import ${e.source}`}</Badge>}
          </div>
          {e.description && <p className="text-sm text-muted-foreground mt-1">{e.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!validated && <button data-testid={`validate-${e.id}`} onClick={onValidate} title="Valider" className="text-muted-foreground hover:text-primary"><Check size={17} /></button>}
          {validated && !e.reversed && <button data-testid={`reverse-${e.id}`} onClick={onReverse} title="Contre-passer" className="text-muted-foreground hover:text-yellow-500"><RotateCcw size={16} /></button>}
          {!validated && <button data-testid={`delete-${e.id}`} onClick={onDelete} title="Supprimer" className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left border-b border-border">
            <th className="py-1.5 pr-2">Compte</th><th className="py-1.5 pr-2">Libellé</th><th className="py-1.5 pr-2 text-right">Débit</th><th className="py-1.5 text-right">Crédit</th></tr></thead>
          <tbody>
            {(e.lines || []).map((l, i) => (
              <tr key={i} className="border-b border-border/40">
                <td className="py-1.5 pr-2 font-mono text-xs">{l.account_code} <span className="text-muted-foreground">{l.account_name}</span></td>
                <td className="py-1.5 pr-2 text-muted-foreground">{l.label}</td>
                <td className="py-1.5 pr-2 text-right">{l.debit ? euro(l.debit) : ""}</td>
                <td className="py-1.5 text-right">{l.credit ? euro(l.credit) : ""}</td>
              </tr>
            ))}
            <tr className="font-bold"><td /><td className="py-1.5 text-right text-xs uppercase text-muted-foreground">Totaux</td>
              <td className="py-1.5 pr-2 text-right">{euro(e.total_debit)}</td><td className="py-1.5 text-right">{euro(e.total_credit)}</td></tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EntryModal({ accounts, onClose, onSaved }) {
  const [journal, setJournal] = useState("recettes");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([{ account_code: "", label: "", debit: "", credit: "" }, { account_code: "", label: "", debit: "", credit: "" }]);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { account_code: "", label: "", debit: "", credit: "" }]);
  const rmLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));
  const totalD = useMemo(() => lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0), [lines]);
  const totalC = useMemo(() => lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0), [lines]);
  const balanced = Math.abs(totalD - totalC) < 0.005 && totalD > 0;

  const submit = async (validate) => {
    const payload = {
      journal, date, ref, description, validate,
      lines: lines.filter((l) => l.account_code && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0))
        .map((l) => { const a = accounts.find((x) => x.code === l.account_code); return { account_code: l.account_code, account_name: a?.name || "", label: l.label, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 }; }),
    };
    try { await api.post("/accounting/entries", payload); toast.success(validate ? "Écriture validée" : "Brouillon enregistré"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Modal open onClose={onClose} title="Nouvelle écriture" testid="entry-modal">
      <div className="grid grid-cols-3 gap-3">
        <Field label="Journal"><Select data-testid="entry-journal" value={journal} onChange={(e) => setJournal(e.target.value)}>{Object.entries(JOURNALS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
        <Field label="Date"><Input data-testid="entry-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Référence"><Input data-testid="entry-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Pièce n°" /></Field>
      </div>
      <Field label="Description"><Input data-testid="entry-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Objet de l'écriture" /></Field>

      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Lignes (Débit = Crédit)</label>
      <div className="space-y-2 mb-2">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`entry-line-${i}`}>
            <select data-testid={`line-account-${i}`} value={l.account_code} onChange={(e) => setLine(i, "account_code", e.target.value)}
              className="flex-1 min-w-0 h-9 px-2 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary">
              <option value="">Compte…</option>
              {accounts.filter((a) => !a.archived).map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </select>
            <Input data-testid={`line-label-${i}`} value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} placeholder="Libellé" className="w-28" />
            <Input data-testid={`line-debit-${i}`} type="number" step="0.01" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} placeholder="Débit" className="w-24" />
            <Input data-testid={`line-credit-${i}`} type="number" step="0.01" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} placeholder="Crédit" className="w-24" />
            {lines.length > 2 && <button type="button" onClick={() => rmLine(i)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={15} /></button>}
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" onClick={addLine} className="mb-3"><Plus size={14} /> Ligne</Button>

      <div className={`flex items-center justify-between rounded-lg px-3 py-2 mb-4 text-sm font-bold ${balanced ? "bg-primary/10 text-primary" : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"}`} data-testid="entry-balance">
        <span className="inline-flex items-center gap-1.5">{balanced ? <ShieldCheck size={15} /> : <AlertTriangle size={15} />} Débit {euro(totalD)} · Crédit {euro(totalC)}</span>
        <span>{balanced ? "Équilibré" : "Déséquilibré"}</span>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => submit(false)}>Enregistrer brouillon</Button>
        <Button type="button" className="flex-1" data-testid="entry-validate-btn" disabled={!balanced} onClick={() => submit(true)}><Check size={16} /> Valider</Button>
      </div>
    </Modal>
  );
}

/* ---------------- Plan comptable ---------------- */
function PlanComptable() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const load = () => api.get("/accounting/accounts").then((r) => setAccounts(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); try { await api.post("/accounting/accounts", form); toast.success("Compte ajouté"); setOpen(false); setForm({ code: "", name: "" }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } };
  const rename = async (a) => { const name = window.prompt("Nouveau libellé", a.name); if (name == null) return; await api.put(`/accounting/accounts/${a.id}`, { name }); load(); };
  const remove = async (a) => { if (!window.confirm(`Supprimer / archiver le compte ${a.code} ?`)) return; const { data } = await api.delete(`/accounting/accounts/${a.id}`); toast.success(data.archived ? "Compte archivé (utilisé dans des écritures)" : "Compte supprimé"); load(); };
  const byKlass = useMemo(() => { const g = {}; accounts.forEach((a) => { (g[a.klass] = g[a.klass] || []).push(a); }); return g; }, [accounts]);

  return (
    <div data-testid="compta-plan">
      <div className="flex justify-end mb-4"><Button data-testid="add-account-btn" onClick={() => setOpen(true)}><Plus size={16} /> Nouveau compte</Button></div>
      {[1, 2, 3, 4, 5, 6, 7].filter((k) => byKlass[k]?.length).map((k) => (
        <div key={k} className="mb-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{KLASS_LABEL[k]}</h3>
          <Card className="p-0 overflow-hidden">
            {byKlass[k].map((a) => (
              <div key={a.id} data-testid={`account-${a.code}`} className={`flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border/40 ${a.archived ? "opacity-50" : ""}`}>
                <div className="min-w-0"><span className="font-mono text-sm font-bold">{a.code}</span> <span className="text-sm">{a.name}</span>{a.archived && <span className="ml-2 text-[11px] text-muted-foreground">(archivé)</span>}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => rename(a)} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                  <button onClick={() => remove(a)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </Card>
        </div>
      ))}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau compte">
        <form onSubmit={add}>
          <Field label="Code (le 1er chiffre = classe)"><Input data-testid="account-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex. 615" required /></Field>
          <Field label="Libellé"><Input data-testid="account-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Frais de tournoi" required /></Field>
          <Button data-testid="save-account-btn" className="w-full mt-2">Ajouter</Button>
        </form>
      </Modal>
    </div>
  );
}

/* ---------------- États financiers (Bilan + Compte de résultats) ---------------- */
function Etats() {
  const [year, setYear] = useState(currentYear);
  const [drafts, setDrafts] = useState(false);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    api.get("/accounting/reports", { params: { year, include_drafts: drafts } })
      .then((r) => setRep(r.data)).catch(() => setRep(null)).finally(() => setLoading(false));
  }, [year, drafts]);
  const r = rep?.result, b = rep?.balance;
  return (
    <div data-testid="compta-etats">
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-28"><label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Exercice</label>
            <Input data-testid="etats-year" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" /></div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground pb-2">
            <input type="checkbox" data-testid="etats-drafts" checked={drafts} onChange={(e) => setDrafts(e.target.checked)} /> Inclure les brouillons
          </label>
          <p className="text-xs text-muted-foreground ml-auto pb-2">Basé sur les écritures {drafts ? "validées + brouillons" : "validées"} de l'exercice {year}.</p>
        </div>
      </Card>

      {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Calcul...</p> : !rep ? <Empty text="Aucune donnée." /> : (
        <div className="space-y-8">
          {/* Compte de résultats */}
          <div>
            <h3 className="text-lg font-extrabold mb-3">Compte de résultats {year}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card data-testid="cr-charges">
                <p className="text-xs font-bold uppercase tracking-widest text-destructive mb-3">Charges (classe 6)</p>
                <LineList items={r.charges} empty="Aucune charge" />
                <TotalRow label="Total charges" value={r.total_charges} color="text-destructive" />
              </Card>
              <Card data-testid="cr-produits">
                <p className="text-xs font-bold uppercase tracking-widest text-green-600 dark:text-green-400 mb-3">Produits (classe 7)</p>
                <LineList items={r.produits} empty="Aucun produit" />
                <TotalRow label="Total produits" value={r.total_produits} color="text-green-600 dark:text-green-400" />
              </Card>
            </div>
            <div className={`mt-4 rounded-xl p-4 flex items-center justify-between font-extrabold text-lg ${r.resultat >= 0 ? "bg-green-500/10 text-green-600 dark:text-green-400" : "bg-destructive/10 text-destructive"}`} data-testid="cr-resultat">
              <span>Résultat de l'exercice ({r.resultat >= 0 ? "Bénéfice" : "Perte"})</span>
              <span>{euro(r.resultat)}</span>
            </div>
          </div>

          {/* Bilan */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-extrabold">Bilan au 31/12/{year}</h3>
              {b.equilibre ? <Badge color="green"><Check size={11} /> Équilibré</Badge> : <Badge color="red"><AlertTriangle size={11} /> Déséquilibré</Badge>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card data-testid="bilan-actif">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">Actif</p>
                <LineList items={b.actif} empty="Aucun élément d'actif" />
                <TotalRow label="Total de l'actif" value={b.total_actif} color="text-primary" />
              </Card>
              <Card data-testid="bilan-passif">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">Passif</p>
                <LineList items={b.passif} empty="Aucun élément de passif" highlightResult />
                <TotalRow label="Total du passif" value={b.total_passif} color="text-primary" />
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LineList({ items, empty, highlightResult }) {
  if (!items?.length) return <p className="text-sm text-muted-foreground py-2">{empty}</p>;
  return (
    <div className="space-y-1.5">
      {items.map((it) => (
        <div key={it.code} className={`flex items-center justify-between gap-2 text-sm py-1 border-b border-border/30 ${it.is_result ? "font-bold" : ""}`}>
          <span className="min-w-0 truncate"><span className="font-mono text-xs text-muted-foreground">{it.code}</span> {it.name}</span>
          <span className={`shrink-0 ${it.is_result && highlightResult ? (it.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-destructive") : ""}`}>{euro(it.amount)}</span>
        </div>
      ))}
    </div>
  );
}
function TotalRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-border font-extrabold">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={color}>{euro(value)}</span>
    </div>
  );
}

/* ---------------- Importer ---------------- */
function Importer() {
  const [source, setSource] = useState("financier");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); setSel({}); api.get("/accounting/importable", { params: { source } }).then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [source]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allSel = items.length > 0 && items.every((it) => sel[it.source_id]);
  const toggleAll = () => { if (allSel) setSel({}); else { const s = {}; items.forEach((it) => { s[it.source_id] = true; }); setSel(s); } };
  const doImport = async () => {
    const chosen = items.filter((it) => sel[it.source_id]);
    if (!chosen.length) { toast.error("Sélectionnez au moins une ligne"); return; }
    try { const { data } = await api.post("/accounting/import", { source, items: chosen }); toast.success(`${data.created} écriture(s) importée(s) en brouillon`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div data-testid="compta-import">
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-56"><label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Source</label>
            <Select data-testid="import-source" value={source} onChange={(e) => setSource(e.target.value)}><option value="financier">Financier (Trésorerie club)</option><option value="comite">Gestion du comité</option></Select></div>
          <p className="text-xs text-muted-foreground ml-auto">{items.length} ligne(s) importable(s)</p>
          <Button data-testid="import-btn" onClick={doImport}><Download size={15} /> Importer la sélection</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Les lignes sélectionnées créent des écritures <b>en brouillon</b> avec une proposition de comptes (recette → Banque/Caisse ⇄ Produit, dépense → Charge ⇄ Banque/Caisse). Vous ajustez les comptes puis validez dans l'onglet Journaux.</p>
      </Card>
      {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Chargement...</p>
        : !items.length ? <Empty text="Aucune ligne à importer (toutes déjà importées)." />
        : (
          <Card className="p-0 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
              <input type="checkbox" data-testid="import-select-all" checked={allSel} onChange={toggleAll} />
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Tout sélectionner</span>
            </div>
            {items.map((it) => (
              <label key={it.source_id} data-testid={`import-item-${it.source_id}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 cursor-pointer hover:bg-secondary/30">
                <input type="checkbox" checked={!!sel[it.source_id]} onChange={() => toggle(it.source_id)} />
                <span className="w-20 text-xs">{fmtDate(it.date)}</span>
                <Badge color={it.type === "recette" ? "green" : "red"}>{it.type === "recette" ? "Recette" : "Dépense"}</Badge>
                <span className="text-xs text-muted-foreground w-16">{it.method === "cash" ? "Caisse" : "Banque"}</span>
                <span className="flex-1 text-sm truncate">{it.label}{it.category ? ` · ${it.category}` : ""}</span>
                <span className="font-bold text-sm">{euro(it.amount)}</span>
              </label>
            ))}
          </Card>
        )}
    </div>
  );
}

/* ---------------- Piste d'audit ---------------- */
function AuditTrail() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/accounting/audit").then((r) => setLogs(r.data)); }, []);
  const ACTION = { create: "Création", "create+validate": "Création + validation", validate: "Validation", update: "Modification", reverse: "Contre-passation", "create-reversal": "Écriture d'extourne", "delete-draft": "Suppression brouillon", import: "Import" };
  return (
    <div data-testid="compta-audit">
      {logs.length ? (
        <Card className="p-0 overflow-hidden">
          {logs.map((l) => (
            <div key={l.id} data-testid="audit-row" className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 text-sm">
              <ShieldCheck size={15} className="text-primary shrink-0" />
              <span className="w-40 text-xs text-muted-foreground">{new Date(l.at).toLocaleString("fr-FR")}</span>
              <Badge color="gray">{ACTION[l.action] || l.action}</Badge>
              <span className="flex-1 text-muted-foreground truncate">{l.details}</span>
              <span className="text-xs font-bold">{l.user}</span>
            </div>
          ))}
        </Card>
      ) : <Empty text="Aucun événement d'audit pour l'instant." />}
    </div>
  );
}
